import React from 'react'
import { StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import type { FeePaidBy, OrganizerGrantInput } from '@hooks'
import { parseCommissionPercent } from '@utils'
import { OptionChips } from '../../events/components/OptionChips'

export interface GrantOptionsValue {
  organization: string
  /** "" = platform default. */
  rate: string
  /** "" = platform default. */
  fee: FeePaidBy | ''
}

export const EMPTY_GRANT_OPTIONS: GrantOptionsValue = { organization: '', rate: '', fee: '' }

/** Options of the form -> the API body, or null while a field is invalid. */
export function toGrantInput(value: GrantOptionsValue): OrganizerGrantInput | null {
  const organizationName = value.organization.trim()
  if (organizationName !== '' && organizationName.length < 2) return null
  const bps = value.rate.trim() === '' ? undefined : parseCommissionPercent(value.rate)
  if (bps === null) return null
  return {
    ...(organizationName ? { organizationName } : {}),
    ...(bps !== undefined ? { commissionRateBps: bps } : {}),
    ...(value.fee ? { feePaidBy: value.fee } : {}),
  }
}

interface GrantOptionsProps {
  value: GrantOptionsValue
  onChange: (value: GrantOptionsValue) => void
  testIDPrefix: string
}

/** Optional starting settings of a new organizer: organization name, commission, who pays the fee. */
export const GrantOptions: React.FC<GrantOptionsProps> = ({ value, onChange, testIDPrefix }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const nameInvalid = value.organization.trim() !== '' && value.organization.trim().length < 2
  const rateInvalid = value.rate.trim() !== '' && parseCommissionPercent(value.rate) === null

  return (
    <View style={styles.box}>
      <TextInput
        testID={`${testIDPrefix}-organization`}
        title={t('admin.add.organizationLabel')}
        value={value.organization}
        onChangeText={(organization) => onChange({ ...value, organization })}
        maxLength={120}
        hasError={nameInvalid}
        errorMessage={nameInvalid ? t('admin.organizer.organizationInvalid') : undefined}
      />
      <TextInput
        testID={`${testIDPrefix}-rate`}
        title={t('admin.add.rateLabel')}
        value={value.rate}
        onChangeText={(rate) => onChange({ ...value, rate })}
        keyboardType="decimal-pad"
        hasError={rateInvalid}
        errorMessage={rateInvalid ? t('admin.organizer.commissionInvalid') : undefined}
      />
      <OptionChips<FeePaidBy>
        testID={`${testIDPrefix}-fee`}
        label={t('admin.organizer.feePaidBy')}
        options={[
          { value: 'attendee', label: t('admin.organizer.feeAttendee') },
          { value: 'organizer', label: t('admin.organizer.feeOrganizer') },
        ]}
        value={value.fee}
        onChange={(fee) => onChange({ ...value, fee })}
      />
      <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
        {t('admin.add.defaultHint')}
      </Text>
    </View>
  )
}

const getStyles = (theme: Theme) => StyleSheet.create({ box: { gap: theme.spacing.md } })
